import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import TermsConsentModal from '../components/TermsConsentModal';
import { useAuth } from '../contexts/AuthContext';

const PhoneLogin: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setTokens } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [isLoading, setIsLoading] = useState(false);
  const isVerifyingRef = useRef(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as US phone number
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Show terms modal if not yet accepted
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }

    await proceedWithSendOtp();
  };

  const proceedWithSendOtp = async () => {
    setIsLoading(true);

    try {
      // Remove formatting for API call
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;

      await axios.post('/auth/phone/send-otp', {
        phoneNumber: formattedPhone,
      });

      toast.success(t('phoneLogin.codeSent', 'Verification code sent!'));
      setStep('otp');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('phoneLogin.failedToSend', 'Failed to send code'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    // Proceed with sending OTP after accepting terms
    proceedWithSendOtp();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission using ref
    if (isVerifyingRef.current || isLoading) return;

    isVerifyingRef.current = true;
    setIsLoading(true);

    try {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;

      const response = await axios.post('/auth/phone/verify-otp', {
        phoneNumber: formattedPhone,
        otp,
      });

      // Check if we got tokens back (success case)
      if (response.data.accessToken && response.data.refreshToken) {
        // Store tokens and update AuthContext state (critical for ProtectedRoute)
        setTokens(response.data.accessToken, response.data.refreshToken);

        // Persist SMS consent to database if terms were accepted
        if (termsAccepted) {
          try {
            await axios.post(
              '/sms-consent/signup',
              {
                phoneNumber: formattedPhone,
              },
              {
                headers: {
                  Authorization: `Bearer ${response.data.accessToken}`,
                },
              },
            );
          } catch (consentError) {
            console.error('Failed to persist SMS consent:', consentError);
            // Don't block login if consent fails to persist
          }
        }

        toast.success(t('phoneLogin.verifiedSuccessfully', 'Phone verified successfully!'));

        // Navigate immediately - don't reset loading state
        if (response.data.user?.email?.endsWith('@phone.chatnexus.temp')) {
          navigate('/complete-profile');
        } else {
          navigate('/dashboard');
        }
      } else {
        // No tokens means something went wrong
        toast.error(t('phoneLogin.verificationFailed', 'Verification failed. Please try again.'));
        setIsLoading(false);
        isVerifyingRef.current = false;
      }
    } catch (error: any) {
      // Only show error for actual failures
      toast.error(error.response?.data?.message || t('phoneLogin.invalidCode', 'Invalid code'));
      setIsLoading(false);
      isVerifyingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <header className="flex items-center h-14 mt-safe">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
        </header>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Icon */}
          <div className="flex justify-center mb-8 mt-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">📱</span>
            </div>
          </div>

          {step === 'phone' ? (
            <>
              {/* Title */}
              <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text text-center mb-2">
                {t('phoneLogin.enterPhoneNumber', 'Enter your phone number')}
              </h1>

              {/* Subtitle */}
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mb-8">
                {t('phoneLogin.sendVerificationCode', "We'll send you a verification code")}
              </p>

              {/* Phone Form */}
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
                  >
                    {t('phoneLogin.phoneNumber', 'Phone Number')}
                  </label>
                  <div className="flex items-center bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border rounded-xl">
                    <span className="px-4 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      🇺🇸 +1
                    </span>
                    <input
                      id="phone"
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      placeholder={t('phoneLogin.phonePlaceholder', '(555) 123-4567')}
                      className="flex-1 px-4 py-3 bg-transparent text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* SMS Compliance Disclosure */}
                <div className="p-3 bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 border border-dark-border dark:border-dark-border border-light-border rounded-xl">
                  <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary leading-relaxed">
                    {t(
                      'phoneLogin.smsDisclosure',
                      'By providing your phone number, you agree to receive SMS messages from Chat Nexus. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not required for purchase.',
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <a
                      href="/sms-terms"
                      className="text-primary-500 hover:text-primary-400 underline"
                    >
                      {t('phoneLogin.smsTerms', 'SMS Terms')}
                    </a>
                    <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      •
                    </span>
                    <a
                      href="/privacy"
                      className="text-primary-500 hover:text-primary-400 underline"
                    >
                      {t('phoneLogin.privacyPolicy', 'Privacy Policy')}
                    </a>
                    <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      •
                    </span>
                    <a href="/terms" className="text-primary-500 hover:text-primary-400 underline">
                      {t('phoneLogin.termsOfService', 'Terms of Service')}
                    </a>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || phoneNumber.replace(/\D/g, '').length < 10}
                  className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? t('phoneLogin.sending', 'Sending...')
                    : t('phoneLogin.sendCode', 'Send Code')}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Title */}
              <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text text-center mb-2">
                {t('phoneLogin.enterVerificationCode', 'Enter verification code')}
              </h1>

              {/* Subtitle */}
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mb-8">
                {t('phoneLogin.sentCodeTo', 'We sent a 6-digit code to {{phone}}', {
                  phone: phoneNumber,
                })}
              </p>

              {/* OTP Form */}
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
                  >
                    {t('phoneLogin.verificationCode', 'Verification Code')}
                  </label>
                  <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('phoneLogin.codePlaceholder', '123456')}
                    className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white text-center text-2xl tracking-widest placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    disabled={isLoading}
                    autoFocus
                    maxLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? t('phoneLogin.verifying', 'Verifying...')
                    : t('phoneLogin.verifyAndContinue', 'Verify & Continue')}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                  }}
                  className="w-full text-primary-500 hover:text-primary-400 text-sm"
                  disabled={isLoading}
                >
                  {t('phoneLogin.changePhoneNumber', 'Change phone number')}
                </button>
              </form>
            </>
          )}

          {/* Footer */}
          <div className="mt-auto pt-8">
            <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center">
              {t('phoneLogin.byContinuing', 'By continuing, you agree to our')}{' '}
              <a href="/terms" className="text-primary-500 underline">
                {t('phoneLogin.termsOfService', 'Terms of Service')}
              </a>{' '}
              {t('phoneLogin.and', 'and')}{' '}
              <a href="/privacy" className="text-primary-500 underline">
                {t('phoneLogin.privacyPolicy', 'Privacy Policy')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Terms Consent Modal */}
      <TermsConsentModal
        isOpen={showTermsModal}
        onAccept={handleAcceptTerms}
        onClose={() => setShowTermsModal(false)}
        allowClose={true}
      />
    </div>
  );
};

export default PhoneLogin;
